import React, { useState, useEffect } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminCustomizationOption } from '@/hooks/admin/useAdmin';
import { useCreateCustomizationOption, useUpdateCustomizationOption } from '@/hooks/admin/useAdminCustomizations';

interface CustomizationOptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    customization?: AdminCustomizationOption;
    onSuccess?: () => void;
}

const CustomizationOptionModal: React.FC<CustomizationOptionModalProps> = ({
    isOpen,
    onClose,
    customization,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        additional_cost: 0,
        sort_order: 0,
        is_active: true
    });

    const createCustomization = useCreateCustomizationOption();
    const updateCustomization = useUpdateCustomizationOption();

    const isEditing = !!customization;
    const isLoading = createCustomization.isPending || updateCustomization.isPending;

    useEffect(() => {
        if (customization) {
            setFormData({
                name: customization.name,
                description: customization.description,
                additional_cost: customization.additional_cost,
                sort_order: customization.sort_order,
                is_active: customization.is_active
            });
        } else {
            setFormData({
                name: '',
                description: '',
                additional_cost: 0,
                sort_order: 0,
                is_active: true
            });
        }
    }, [customization, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre de la opción de personalización es requerido');
            return;
        }

        if (!formData.description.trim()) {
            alert('La descripción de la opción de personalización es requerida');
            return;
        }

        if (formData.additional_cost < 0) {
            alert('El costo adicional debe ser mayor o igual a cero');
            return;
        }

        try {
            if (isEditing && customization) {
                await updateCustomization.mutateAsync({
                    id: customization.id,
                    data: formData
                });
            } else {
                await createCustomization.mutateAsync(formData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving customization option:', error);
            alert('Error al guardar la opción de personalización');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Opción de Personalización' : 'Nueva Opción de Personalización'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <Input
                                label="Nombre"
                                name="name"
                                value={formData.name}
                                setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                placeholder="Ingrese el nombre de la opción"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Costo Adicional"
                                name="additional_cost"
                                type="number"
                                step="0.01"
                                value={formData.additional_cost.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, additional_cost: parseFloat(value) || 0 }))}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Orden de Visualización"
                                name="sort_order"
                                type="number"
                                value={formData.sort_order.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, sort_order: parseInt(value) || 0 }))}
                                placeholder="0"
                            />
                        </div>

                        <div className="admin-form-group admin-form-full">
                            <Input
                                label="Descripción"
                                name="description"
                                value={formData.description}
                                setValue={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                placeholder="Descripción detallada de la opción de personalización"
                                rows={4}
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <span>Opción Activa</span>
                            </label>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Opción
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CustomizationOptionModal;
